import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../lib/toast';

// Reassignment.
//
// A release answers the subgrantee's question and leaves the state's unanswered: the
// home is still unserved and still the state's obligation. This screen is where that
// obligation gets discharged - by finding the operator whose plant already runs near
// the cluster somebody just handed back, pricing what it would cost THEM, and making
// an offer.
//
// It reads left to right the way the work goes: what came back, who is near it, what
// it would cost them, what the state pays.

const usd = (n: any) =>
  n == null || !Number.isFinite(Number(n)) ? '—'
    : Number(n).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const STATUS_PILL: Record<string, string> = {
  open: 'pill pill--info',
  accepted: 'pill pill--success',
  declined: 'pill pill--danger',
  withdrawn: 'pill',
  expired: 'pill pill--warning',
};

export default function ReassignmentPage() {
  const qc = useQueryClient();
  const [pick, setPick] = useState<string | null>(null);
  const [priced, setPriced] = useState<Record<string, any>>({});
  const [offerTo, setOfferTo] = useState<any | null>(null);
  const [price, setPrice] = useState('');
  const [basis, setBasis] = useState('engine_marginal');
  const [terms, setTerms] = useState('');

  const poolQ = useQuery<any>({
    queryKey: ['ra-pool'],
    queryFn: () => api.get('/descope/reassignment/pool'),
  });
  const offersQ = useQuery<any[]>({
    queryKey: ['ra-offers'],
    queryFn: () => api.get('/descope/reassignment/offers'),
  });

  const pool: any[] = poolQ.data?.pool ?? [];
  const selected = useMemo(
    () => pool.find((p) => (p.decision_id ?? p.project_id) === pick) ?? null, [pool, pick]);

  const candQ = useQuery<any>({
    queryKey: ['ra-cand', pick],
    queryFn: () => api.post('/descope/reassignment/candidates', {
      from_project_id: selected.project_id, location_ids: selected.location_ids,
    }),
    enabled: !!selected,
  });

  const priceOne = useMutation({
    mutationFn: (c: any) => api.post('/descope/reassignment/price', {
      from_project_id: selected.project_id,
      to_project_id: c.project_id,
      location_ids: selected.location_ids,
    }),
    onSuccess: (r: any, c: any) => {
      setPriced((prev) => ({ ...prev, [c.project_id]: r }));
      if (r?.usable) toast.success(`${c.subgrantee_label}: ${usd(r.total_cost_usd)}`);
      else toast.error(r?.message ?? 'Could not price this candidate');
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const makeOffer = useMutation({
    mutationFn: () => api.post('/descope/reassignment/offers', {
      from_project_id: selected.project_id,
      to_project_id: offerTo.project_id,
      location_ids: selected.location_ids,
      price_usd: Number(price),
      price_basis: basis,
      terms: terms || null,
      estimated_marginal_usd: priced[offerTo.project_id]?.total_cost_usd ?? null,
      estimate_review_id: priced[offerTo.project_id]?.review_id ?? null,
    }),
    onSuccess: (r: any) => {
      toast.success(`Offer ${r.offer_no} sent`);
      setOfferTo(null); setPrice(''); setTerms('');
      qc.invalidateQueries({ queryKey: ['ra-pool'] });
      qc.invalidateQueries({ queryKey: ['ra-offers'] });
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const respond = useMutation({
    mutationFn: ({ id, action }: any) =>
      api.post(`/descope/reassignment/offers/${id}/respond`, { action }),
    onSuccess: (r: any) => {
      toast.success(`${r.offer_no} ${r.status}`);
      qc.invalidateQueries({ queryKey: ['ra-pool'] });
      qc.invalidateQueries({ queryKey: ['ra-offers'] });
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const offers = offersQ.data ?? [];
  // What the state has actually done. Accepted offers only: an open offer has moved
  // nobody and paid nobody.
  const settled = offers.filter((o: any) => o.status === 'accepted');
  const paidOut = settled.reduce((a: number, o: any) => a + Number(o.price_usd ?? 0), 0);
  const servedAgain = settled.reduce((a: number, o: any) => a + Number(o.locations ?? 0), 0);

  return (
    <div style={{ padding: 24, maxWidth: 1500 }}>
      <h1 className="page__title" style={{ margin: '0 0 4px' }}>Reassignment</h1>
      <p className="page__subtitle" style={{ marginTop: 0 }}>
        Locations released by determination are still unserved and still this office's
        obligation. Find the operator whose plant already runs near them.
      </p>

      <div className="bead-kpis" style={{ marginBottom: 18 }}>
        <div className="bead-kpi">
          <div className="bead-kpi__label">Awaiting a home</div>
          <div className="bead-kpi__value">{pool.reduce((a, p) => a + p.unassigned, 0)}</div>
        </div>
        <div className="bead-kpi">
          <div className="bead-kpi__label">Reassigned</div>
          <div className="bead-kpi__value">{servedAgain}</div>
        </div>
        <div className="bead-kpi">
          <div className="bead-kpi__label">Obligated to take them</div>
          <div className="bead-kpi__value">{usd(paidOut)}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── what came back ── */}
        <div className="card">
          <div className="card__header">Released and unassigned</div>
          {poolQ.isLoading ? (
            <div className="card__body" style={{ color: 'var(--text-muted)' }}>Loading…</div>
          ) : pool.length === 0 ? (
            <div className="card__body" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Nothing is waiting. Every released location has been reassigned or is on a
              live offer.
            </div>
          ) : pool.map((p) => (
            <div key={p.decision_id ?? p.project_id}
                 onClick={() => { setPick(p.decision_id ?? p.project_id); setOfferTo(null); }}
                 style={{
                   padding: '12px 16px', cursor: 'pointer',
                   borderTop: '1px solid var(--border)',
                   background: (p.decision_id ?? p.project_id) === pick ? 'var(--surface-alt)' : undefined,
                 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ flex: 1 }}>{p.subgrantee_label}</b>
                <span className="pill pill--warning">{p.unassigned}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                {p.determination_no ?? p.project_code}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                released {String(p.decided_at ?? p.oldest_release ?? '').slice(0, 10)}
              </div>
            </div>
          ))}
        </div>

        {/* ── who is near them ── */}
        <div>
          {!selected ? (
            <div className="card"><div className="card__body" style={{ color: 'var(--text-muted)' }}>
              Pick a released set on the left to see who could serve it.
            </div></div>
          ) : (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card__header">
                <span>Who could serve {selected.unassigned} released from {selected.subgrantee_label}</span>
              </div>
              {candQ.isLoading ? (
                <div className="card__body" style={{ color: 'var(--text-muted)' }}>Measuring…</div>
              ) : (candQ.data?.candidates ?? []).length === 0 ? (
                <div className="card__body" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  {candQ.data?.note ?? 'No other operator on this system has plant to measure against.'}
                </div>
              ) : (
                <table className="table" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead><tr>
                    <th>Operator</th>
                    <th style={{ textAlign: 'right' }}>Their plant</th>
                    <th style={{ textAlign: 'right' }}>Would cost them</th>
                    <th style={{ textAlign: 'right' }}>Per passing</th>
                    <th />
                  </tr></thead>
                  <tbody>
                    {(candQ.data?.candidates ?? []).map((c: any) => {
                      const pr = priced[c.project_id];
                      const busy = priceOne.isPending && priceOne.variables?.project_id === c.project_id;
                      return (
                        <tr key={c.project_id}>
                          <td>
                            <b>{c.subgrantee_label}</b>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                              {c.project_code}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>{c.miles_to_plant} mi</b>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>away</div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {pr?.usable ? <b>{usd(pr.total_cost_usd)}</b>
                              : <span style={{ color: 'var(--text-muted)' }}>not priced</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {pr?.usable ? usd(pr.cost_per_passing_usd) : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <button className="btn" disabled={priceOne.isPending}
                                    onClick={() => priceOne.mutate(c)}>
                              {busy ? 'Designing…' : pr ? 'Re-price' : 'Price it'}
                            </button>{' '}
                            <button className="btn btn--primary"
                                    onClick={() => {
                                      setOfferTo(c);
                                      setPrice(String(Math.round(pr?.total_cost_usd ?? 0) || ''));
                                      setBasis(pr?.usable ? 'engine_marginal' : 'negotiated');
                                    }}>
                              Offer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div className="card__body" style={{ fontSize: 12, color: 'var(--text-muted)', paddingTop: 10 }}>
                Distance is measured to each operator's own plant. Pricing designs fibre
                from THEIR network to these locations — same engine, same rate card as the
                review that released them.
              </div>
            </div>
          )}

          {/* ── the offer ── */}
          {offerTo && selected && (
            <div className="card" style={{ marginBottom: 16, borderColor: 'var(--primary)' }}>
              <div className="card__header">
                Offer {selected.unassigned} locations to {offerTo.subgrantee_label}
              </div>
              <div className="card__body" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
                    The state pays
                  </div>
                  <input value={price} onChange={(e) => setPrice(e.target.value)}
                         inputMode="numeric" style={{ width: 160 }} placeholder="0" />
                </div>
                <div>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
                    On what basis
                  </div>
                  <select value={basis} onChange={(e) => setBasis(e.target.value)}>
                    <option value="engine_marginal">Our engineering estimate</option>
                    <option value="negotiated">Negotiated</option>
                    <option value="subgrantee_quote">Their quote</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
                    Terms
                  </div>
                  <input value={terms} onChange={(e) => setTerms(e.target.value)}
                         style={{ width: '100%' }}
                         placeholder="Milestone, deadline — this goes on the record" />
                </div>
                <button className="btn" onClick={() => setOfferTo(null)}>Cancel</button>
                <button className="btn btn--primary"
                        disabled={makeOffer.isPending || !Number(price)}
                        onClick={() => makeOffer.mutate()}>
                  {makeOffer.isPending ? 'Sending…' : 'Send offer'}
                </button>
              </div>
              {priced[offerTo.project_id]?.usable && (
                <div className="card__body" style={{ paddingTop: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                  Our estimate was {usd(priced[offerTo.project_id].total_cost_usd)} over{' '}
                  {priced[offerTo.project_id].reached} of {priced[offerTo.project_id].requested}{' '}
                  reached, from plant {priced[offerTo.project_id].miles_to_plant} mi away. That
                  design is kept — the offer cites it.
                </div>
              )}
            </div>
          )}

          {/* ── what has been offered ── */}
          <div className="card">
            <div className="card__header">Offers</div>
            {offers.length === 0 ? (
              <div className="card__body" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                No offers yet.
              </div>
            ) : (
              <table className="table" style={{ fontVariantNumeric: 'tabular-nums' }}>
                <thead><tr>
                  <th>Offer</th><th>From</th><th>To</th>
                  <th style={{ textAlign: 'right' }}>Locations</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th style={{ textAlign: 'right' }}>Our estimate</th>
                  <th>Status</th><th />
                </tr></thead>
                <tbody>
                  {offers.map((o: any) => (
                    <tr key={o.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'nowrap' }}>
                        <b>{o.offer_no}</b>
                      </td>
                      <td>{o.from_label}</td>
                      <td>{o.to_label}</td>
                      <td style={{ textAlign: 'right' }}>{o.locations}</td>
                      <td style={{ textAlign: 'right' }}>
                        <b>{usd(o.price_usd)}</b>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {String(o.price_basis ?? '').replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                        {usd(o.estimated_marginal_usd)}
                      </td>
                      <td><span className={STATUS_PILL[o.status] ?? 'pill'}>{o.status}</span></td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {o.status === 'open' && (
                          <>
                            <button className="btn" disabled={respond.isPending}
                                    onClick={() => respond.mutate({ id: o.id, action: 'withdraw' })}>
                              Withdraw
                            </button>{' '}
                            <button className="btn" disabled={respond.isPending}
                                    onClick={() => respond.mutate({ id: o.id, action: 'decline' })}>
                              Declined
                            </button>{' '}
                            <button className="btn btn--primary" disabled={respond.isPending}
                                    onClick={() => respond.mutate({ id: o.id, action: 'accept' })}>
                              Accepted
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="card__body" style={{ fontSize: 12, color: 'var(--text-muted)', paddingTop: 10 }}>
              Accepting moves the locations onto the receiving award and writes the
              obligation to the amendment ledger — the same ledger the release was
              recorded in, so the state's net position is the arithmetic between them.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
